/**
 * Contacts page — parameterized. Same code drives /books/customers and /books/vendors.
 */
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Loader2, Plus, Search, Trash2, X, Users } from 'lucide-react';
import { booksClient, errMsg, stripEmpty } from './api';
import { toast } from '../hooks/use-toast';

const emptyForm = (type) => ({
  type, name: '', display_name: '', email: '', phone: '',
  tax_registration: '', billing_address: '', payment_terms_days: 30, default_tax_code: '', notes: '',
});

const ContactsPage = ({ type = 'customer' }) => {
  const location = useLocation();
  const effectiveType = location.pathname.includes('vendors') ? 'vendor' : type;
  const label = effectiveType === 'customer' ? 'Customers' : 'Vendors';

  const [items, setItems] = useState([]);
  const [taxCodes, setTaxCodes] = useState([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [openNew, setOpenNew] = useState(false);
  const [form, setForm] = useState(emptyForm(effectiveType));

  const load = () => {
    setLoading(true);
    booksClient.get('/contacts', { params: { type: effectiveType, q: q || undefined } })
      .then((r) => setItems(r.data.data || [])).catch(() => setItems([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    setForm(emptyForm(effectiveType));
    load();
    booksClient.get('/masters/tax-codes').then((r) => setTaxCodes(r.data.data || []));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveType]);

  const submit = async () => {
    try {
      await booksClient.post('/contacts', stripEmpty(form));
      setOpenNew(false); setForm(emptyForm(effectiveType));
      toast({ title: `${label.slice(0, -1)} created` });
      load();
    } catch (e) { toast({ title: errMsg(e), variant: 'destructive' }); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this contact?')) return;
    try {
      await booksClient.delete(`/contacts/${id}`);
      toast({ title: 'Deleted' });
      load();
    } catch (e) { toast({ title: errMsg(e), variant: 'destructive' }); }
  };

  return (
    <div>
      <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div>
          <div className="text-xs font-semibold tracking-[0.3em] text-[#0f5c3d] uppercase">Contacts</div>
          <h1 className="text-4xl text-[#0b1a2e]" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 500 }}>{label}</h1>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && load()}
              placeholder="Search…" data-testid="contacts-search"
              className="pl-9 pr-3 py-2 rounded-lg border border-slate-300 text-sm bg-white" />
          </div>
          <button onClick={() => setOpenNew(true)} data-testid="new-contact-btn"
            className="inline-flex items-center gap-1 px-4 py-2 bg-[#0f5c3d] text-white rounded-full text-sm font-semibold">
            <Plus className="w-4 h-4" /> New
          </button>
        </div>
      </div>

      {loading ? <div className="p-12 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></div> :
      items.length === 0 ? (
        <div className="p-12 text-center text-[#0b1a2e]/40 italic bg-white rounded-2xl border border-black/5">
          <Users className="w-8 h-8 mx-auto mb-2 opacity-40" />
          No {label.toLowerCase()} yet — add your first one.
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-black/5 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#f7fbf8] text-[10px] uppercase tracking-widest text-[#0b1a2e]/60">
              <tr><th className="text-left px-4 py-2">Name</th><th className="text-left px-4 py-2">Email</th><th className="text-left px-4 py-2">Tax No.</th><th className="text-right px-4 py-2">Terms</th><th className="w-8"></th></tr>
            </thead>
            <tbody>
              {items.map((c) => (
                <tr key={c.contact_id} className={`border-t border-slate-100 hover:bg-[#f7fbf8] ${!c.is_active ? 'opacity-50' : ''}`}>
                  <td className="px-4 py-2 text-[#0b1a2e] font-medium">
                    {c.display_name || c.name}
                    {!c.is_active && <span className="ml-2 text-[10px] uppercase tracking-widest text-red-600">inactive</span>}
                  </td>
                  <td className="px-4 py-2 text-[#0b1a2e]/70">{c.email || ''}</td>
                  <td className="px-4 py-2 font-mono text-xs">{c.tax_registration || ''}</td>
                  <td className="px-4 py-2 text-right text-xs text-[#0b1a2e]/70">{c.payment_terms_days || 30} days</td>
                  <td className="px-4 py-2">
                    <button onClick={() => del(c.contact_id)} className="text-red-600 hover:text-red-700"><Trash2 className="w-3.5 h-3.5" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {openNew && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg p-6 max-h-[90vh] overflow-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-medium text-[#0b1a2e]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>New {label.slice(0, -1)}</h2>
              <button onClick={() => setOpenNew(false)}><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-3">
              <F label="Name *"><input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="contact-name" className="w-full px-3 py-2 border border-slate-300 rounded text-sm" /></F>
              <div className="grid grid-cols-2 gap-3">
                <F label="Email"><input type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} className="w-full px-3 py-2 border border-slate-300 rounded text-sm" /></F>
                <F label="Phone"><input value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} className="w-full px-3 py-2 border border-slate-300 rounded text-sm" /></F>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <F label="Tax registration"><input value={form.tax_registration} onChange={(e) => setForm((f) => ({ ...f, tax_registration: e.target.value }))} placeholder="VAT/GST No." className="w-full px-3 py-2 border border-slate-300 rounded text-sm" /></F>
                <F label="Payment terms (days)"><input type="number" value={form.payment_terms_days} onChange={(e) => setForm((f) => ({ ...f, payment_terms_days: parseInt(e.target.value, 10) || 30 }))} className="w-full px-3 py-2 border border-slate-300 rounded text-sm" /></F>
              </div>
              <F label="Default tax code">
                <select value={form.default_tax_code} onChange={(e) => setForm((f) => ({ ...f, default_tax_code: e.target.value }))} data-testid="contact-default-tax"
                  className="w-full px-3 py-2 border border-slate-300 rounded text-sm">
                  <option value="">— None —</option>
                  {taxCodes.map((t) => <option key={t.code} value={t.code}>{t.code} — {t.name}</option>)}
                </select>
              </F>
              <F label="Billing address"><textarea value={form.billing_address} onChange={(e) => setForm((f) => ({ ...f, billing_address: e.target.value }))} rows={2} className="w-full px-3 py-2 border border-slate-300 rounded text-sm" /></F>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setOpenNew(false)} className="px-4 py-2 text-sm text-[#0b1a2e]/70">Cancel</button>
              <button onClick={submit} disabled={!form.name} data-testid="contact-save"
                className="px-5 py-2 bg-[#0f5c3d] text-white rounded-full text-sm font-semibold disabled:opacity-50">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const F = ({ label, children }) => (
  <label className="block">
    <div className="text-[10px] font-semibold uppercase tracking-widest text-[#0b1a2e]/60 mb-1">{label}</div>
    {children}
  </label>
);

export default ContactsPage;
